"use client";

import { useCallback, useMemo } from "react";
import type { DeleteOriginsRequest, MeiliOrigin } from "@/generated/fastapi";
import { showErrorToast, showSuccessToast } from "@/shared/feedback";
import type { GenericIconType } from "@/shared/ui/actions/GenericIcon";
import { ORIGIN_CONFIGURABLE_ACTIONS } from "../config/originActionPresentation";

export type OriginItemAction = {
  onClick: (origin: MeiliOrigin) => void | Promise<void>;
  icon: GenericIconType;
  humanReadable: string;
  hide?: boolean;
  actionDescription?: string;
  color?: string;
};

export type ResolvedOriginAction = {
  key: string;
  label: string;
  humanReadable: string;
  icon: GenericIconType;
  onClick: () => void | Promise<void>;
  color?: string;
  tone?: "default" | "danger";
};

type UseOriginItemActionsParams = {
  editAction?: OriginItemAction;
  extendAction?: OriginItemAction;
  onDeleteOrigin: (
    request: DeleteOriginsRequest,
    onSuccess?: () => void,
  ) => Promise<unknown>;
  onDeleteSearchResultAction?: (originId: string) => void;
  origin: MeiliOrigin;
  primaryAction?: OriginItemAction;
  secondaryAction?: OriginItemAction;
  tertiaryAction?: OriginItemAction;
  wasProcessed: boolean;
};

export default function useOriginItemActions({
  editAction,
  extendAction,
  onDeleteOrigin,
  onDeleteSearchResultAction,
  origin,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  wasProcessed,
}: UseOriginItemActionsParams): ResolvedOriginAction[] {
  const handleDeleteSearchResult = useCallback(
    (deletedOriginId: string) => {
      onDeleteSearchResultAction?.(deletedOriginId);
    },
    [onDeleteSearchResultAction],
  );

  const handleDelete = useCallback(async () => {
    await onDeleteOrigin(
      {
        origin_ids: [origin.objectID],
        delete_mode: wasProcessed ? "soft" : "permanent",
      },
      () => handleDeleteSearchResult(origin.objectID),
    );
  }, [handleDeleteSearchResult, onDeleteOrigin, origin.objectID, wasProcessed]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(origin.raw_text_input);
      showSuccessToast("Copied to clipboard");
    } catch {
      showErrorToast("Failed to copy source material");
    }
  }, [origin.raw_text_input]);

  return useMemo<ResolvedOriginAction[]>(() => {
    const actionByKey = {
      extract: primaryAction,
      identify_topics: secondaryAction,
      chat: tertiaryAction,
      edit: editAction,
      extend: extendAction,
    } satisfies Record<
      (typeof ORIGIN_CONFIGURABLE_ACTIONS)[number]["key"],
      OriginItemAction | undefined
    >;

    const configurableActions = ORIGIN_CONFIGURABLE_ACTIONS.flatMap(
      ({ key, defaultLabel }) => {
        const action = actionByKey[key];

        if (!action || action.hide) {
          return [];
        }

        return [
          {
            key,
            label: action.actionDescription ?? defaultLabel,
            humanReadable: action.humanReadable,
            icon: action.icon,
            onClick: () => action.onClick(origin),
            color: action.color,
          },
        ];
      },
    );

    return [
      ...configurableActions,
      {
        key: "copy",
        label: "Copy",
        humanReadable: "Copy source material to clipboard",
        icon: "copy_clipboard" as GenericIconType,
        onClick: handleCopy,
      },
      {
        key: "delete",
        label: wasProcessed ? "Trash" : "Delete",
        humanReadable: wasProcessed
          ? "Move source material to trash"
          : "Delete source material permanently",
        icon: wasProcessed ? "delete_soft" : "delete_permanent",
        onClick: handleDelete,
        tone: "danger",
      },
    ];
  }, [
    editAction,
    extendAction,
    handleCopy,
    handleDelete,
    origin,
    primaryAction,
    secondaryAction,
    tertiaryAction,
    wasProcessed,
  ]);
}
